import {
  BuildingStorefrontIcon,
  ChartBarIcon,
  CheckCircleIcon,
  StarIcon,
  GlobeAltIcon,
  TruckIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

export default function Partnership() {
  const partnershipTypes = [
    {
      title: "Restaurant Partners",
      description: "Join our network of restaurants reducing food waste",
      icon: <BuildingStorefrontIcon className="w-8 h-8" />,
      benefits: [
        "Reduce food waste and disposal costs",
        "Tax benefits for food donations",
        "Positive community impact",
        "Marketing opportunities",
      ],
      requirements: [
        "Valid business license",
        "Food safety certification",
        "Regular donation commitment",
      ],
    },
    {
      title: "Corporate Sponsors",
      description: "Support our mission through corporate partnerships",
      icon: <BuildingStorefrontIcon className="w-8 h-8" />,
      benefits: [
        "CSR and sustainability goals",
        "Brand visibility and recognition",
        "Employee engagement opportunities",
        "Impact reporting and metrics",
      ],
      requirements: [
        "Minimum annual commitment",
        "Alignment with our values",
        "Regular impact reporting",
      ],
    },
    {
      title: "Logistics Partners",
      description: "Help us with transportation and delivery services",
      icon: <TruckIcon className="w-8 h-8" />,
      benefits: [
        "Expand your service reach",
        "Community goodwill",
        "Regular business opportunities",
        "Partnership recognition",
      ],
      requirements: [
        "Reliable transportation",
        "Food safety compliance",
        "Flexible scheduling",
      ],
    },
  ];

  const successMetrics = [
    {
      label: "Partner Restaurants",
      value: "150+",
      icon: <BuildingStorefrontIcon className="w-5 h-5" />,
    },
    {
      label: "Corporate Sponsors",
      value: "25+",
      icon: <BuildingStorefrontIcon className="w-5 h-5" />,
    },
    {
      label: "Logistics Partners",
      value: "40+",
      icon: <TruckIcon className="w-5 h-5" />,
    },
    {
      label: "Cities Covered",
      value: "25",
      icon: <GlobeAltIcon className="w-5 h-5" />,
    },
  ];

  const partnerTestimonials = [
    {
      name: "Chef Priya Rajapaksa",
      company: "Colombo Kitchen",
      role: "Head Chef",
      quote:
        "Partnering with MealBridge has helped us reduce waste by 40% while making a real difference in our community. It's a win-win for everyone.",
      image:
        "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face",
    },
    {
      name: "David Fernando",
      company: "Green Logistics",
      role: "Operations Manager",
      quote:
        "Being a logistics partner with MealBridge has expanded our business while allowing us to contribute to a meaningful cause.",
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
    },
    {
      name: "Sarah Perera",
      company: "TechCorp Sri Lanka",
      role: "CSR Director",
      quote:
        "Our partnership with MealBridge aligns perfectly with our sustainability goals and has been incredibly rewarding for our team.",
      image:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
    },
  ];

  const partnershipProcess = [
    {
      step: 1,
      title: "Initial Contact",
      description:
        "Reach out to us through our partnership form or contact our team directly.",
      icon: <BuildingStorefrontIcon className="w-6 h-6" />,
    },
    {
      step: 2,
      title: "Assessment",
      description:
        "We'll assess your business and discuss how we can work together effectively.",
      icon: <ChartBarIcon className="w-6 h-6" />,
    },
    {
      step: 3,
      title: "Agreement",
      description:
        "Sign a partnership agreement that outlines terms, expectations, and benefits.",
      icon: <CheckCircleIcon className="w-6 h-6" />,
    },
    {
      step: 4,
      title: "Launch",
      description:
        "Start your partnership journey with our support and guidance.",
      icon: <StarIcon className="w-6 h-6" />,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
      {/* Hero Section */}
      <div className="py-16 text-white bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-600">
        <div className="px-4 mx-auto text-center max-w-7xl sm:px-6 lg:px-8">
          <BuildingStorefrontIcon className="w-16 h-16 mx-auto mb-6 text-white" />
          <h1 className="mb-6 text-3xl font-bold md:text-4xl lg:text-5xl">
            Partnership Opportunities
          </h1>
          <p className="max-w-3xl mx-auto text-lg text-white/95 font-medium md:text-xl">
            Join us in creating a sustainable food sharing ecosystem that
            benefits businesses, communities, and the environment.
          </p>
        </div>
      </div>

      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Success Metrics */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              Our Partnership Impact
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              Together with our partners, we're making a real difference across
              Sri Lanka
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {successMetrics.map((metric, index) => (
              <div key={index} className="text-center card-dashboard">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl shadow-lg">
                  {metric.icon}
                </div>
                <h3 className="mb-2 text-2xl font-bold text-slate-900">
                  {metric.value}
                </h3>
                <p className="text-sm font-medium text-slate-600">
                  {metric.label}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Partnership Types */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              Partnership Types
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              Choose the partnership model that best fits your business goals
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {partnershipTypes.map((type, index) => (
              <div key={index} className="card-dashboard">
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl shadow-lg">
                    {type.icon}
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-slate-900">
                    {type.title}
                  </h3>
                  <p className="text-sm text-slate-600">{type.description}</p>
                </div>

                <div className="mb-6">
                  <h4 className="mb-3 text-sm font-bold text-slate-900">
                    Benefits:
                  </h4>
                  <ul className="space-y-2">
                    {type.benefits.map((benefit, benefitIndex) => (
                      <li
                        key={benefitIndex}
                        className="flex items-start space-x-2"
                      >
                        <CheckCircleIcon className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                        <span className="text-xs text-slate-600">
                          {benefit}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="mb-3 text-sm font-bold text-slate-900">
                    Requirements:
                  </h4>
                  <ul className="space-y-2">
                    {type.requirements.map((requirement, reqIndex) => (
                      <li key={reqIndex} className="flex items-start space-x-2">
                        <ShieldCheckIcon className="w-4 h-4 mt-0.5 text-slate-500 flex-shrink-0" />
                        <span className="text-xs text-slate-600">
                          {requirement}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Partnership Process */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              How to Become a Partner
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              Our simple 4-step process makes it easy to start your partnership
              journey
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {partnershipProcess.map((step, index) => (
              <div key={index} className="text-center">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl shadow-lg">
                  {step.icon}
                </div>
                <div className="mb-2 text-lg font-bold text-emerald-600">
                  Step {step.step}
                </div>
                <h3 className="mb-3 text-lg font-bold text-slate-900">
                  {step.title}
                </h3>
                <p className="text-sm text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              What Our Partners Say
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              Hear from businesses that have joined our mission
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {partnerTestimonials.map((testimonial, index) => (
              <div key={index} className="card-dashboard">
                <div className="flex items-center mb-4 space-x-3">
                  <div className="w-12 h-12 overflow-hidden rounded-full">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">
                      {testimonial.name}
                    </h4>
                    <p className="text-xs text-slate-600">
                      {testimonial.role}, {testimonial.company}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 italic">
                  "{testimonial.quote}"
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center">
          <div className="card-dashboard bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200">
            <h2 className="mb-4 text-xl font-bold text-slate-900 lg:text-2xl">
              Ready to Partner With Us?
            </h2>
            <p className="max-w-2xl mx-auto mb-8 text-sm text-slate-700 font-medium">
              Join our growing network of partners and make a meaningful impact
              in your community while growing your business.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                to="/contact"
                className="px-6 py-3 text-sm font-medium text-white bg-emerald-600 rounded-lg border border-emerald-600 text-center"
              >
                Start Partnership
              </Link>
              <Link
                to="/help"
                className="px-6 py-3 text-sm font-medium text-slate-700 bg-white rounded-lg border border-slate-300 text-center"
              >
                Download Partnership Guide
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
